import { highlights, type HighlightColor } from '@vd/tokens';
import { BUG_VARIANTS } from './bugVariants';

const BUG_VARIANT_SET: ReadonlySet<string> = new Set(BUG_VARIANTS);
const BUG_VARIANT_PATTERN = /^[a-z][a-z-]{0,31}$/;

/**
 * Text annotation presentation. `variant` controls the box background
 * (plain = transparent, sticky = the yellow note); `style` controls the
 * text color treatment. Rendering lives in render.ts (`drawTextBox`);
 * the web app's TextBoxModal edits these and re-exports the types.
 */
export const TEXT_VARIANTS = ['plain', 'sticky'] as const;
export type TextVariant = typeof TEXT_VARIANTS[number];
export const TEXT_STYLES = ['red-outline', 'red', 'black', 'white'] as const;
export type TextStyle = typeof TEXT_STYLES[number];
export const HIGHLIGHT_COLORS = Object.freeze(Object.keys(highlights) as HighlightColor[]);
export const PIN_STYLES = ['pin', 'lilypad', 'bug', 'fly', 'numbered-dot'] as const;
export type PinStyle = typeof PIN_STYLES[number];

/**
 * Which bug a `bug` pin is (TOADO-297).
 *
 * Deliberately a field of its own rather than five new `style` values, and
 * the difference is not cosmetic. `style` is validated on three boundaries an
 * older client owns: `render.ts` returns early on an unknown one and the pin
 * draws as nothing; `anchored.ts` fails row validation and drops the pin from
 * the control's response; `captureTicketMetadata.ts` rejects the request, so
 * the whole capture 400s and the reporter loses the bug report they just
 * spent five minutes on. The extension ships through a multi-day store queue
 * while the API deploys on push, so teammates routinely run different builds
 * and all three would fire in the field.
 *
 * With `style` left as `'bug'`, `isPinStyle` passes everywhere and none of
 * them fire. A build that has never heard of the variant renders the generic
 * hand-drawn bug and keeps the value through a save.
 */
export { BUG_VARIANTS } from './bugVariants';
import type { BugVariant as BugVariantName } from './bugVariants';
export type { BugVariant } from './bugVariants';

/**
 * Is this a well-formed variant, whether or not this build knows it?
 *
 * The validation across the boundaries is deliberately uneven, and it is
 * worth stating rather than leaving to be inferred. The capture endpoint and
 * the extension's save protocol REJECT a malformed value, because a client of
 * ours never sends one. The anchored-row parser OMITS a malformed value and
 * keeps the row, because a pin vanishing from the list is worse than a pin
 * drawn generically. And the renderer accepts any string at all, because by
 * then the only consequence of an unknown one is which glyph gets drawn.
 *
 * Deliberately not a membership test. A newer client will write a species
 * this one has never heard of, and every boundary that checks this has to
 * pass it through: the API must not 400 the capture, the control must not
 * drop the row, and the renderer draws the generic bug. Recognition happens
 * once, at draw time, where not recognising it costs nothing.
 *
 * What it does reject is anything that is not a short lowercase token, which
 * is all that is needed of a value that is stored, echoed back to other
 * clients, and used to look up a sprite tile.
 */
export function isBugVariant(value: unknown): value is string {
  return typeof value === 'string' && BUG_VARIANT_PATTERN.test(value);
}

/**
 * What a TICKET TYPE's glyph may look like, which is not which ones exist
 * (TOADO-409).
 *
 * Here rather than in `@vd/types` because it belongs beside `isBugVariant`: it is
 * the same rule for the same reason, and the panel and the API both already
 * depend on this package. Two copies of one pattern is how a boundary quietly
 * stops agreeing with the one next to it.
 *
 * The case is stronger here than for bug species, because a type set is
 * WORKSPACE-EDITABLE. Somebody adds "Tech debt" with a wrench this afternoon and
 * every installed build is older than that, so an unknown glyph is the normal
 * case rather than a forward-compatibility courtesy. Recognition happens once,
 * at draw time, where not recognising one costs a generic marker.
 */
export const TICKET_TYPE_GLYPH_PATTERN = /^[a-z][a-z-]{0,31}$/;

export function isTicketTypeGlyph(value: unknown): value is string {
  return typeof value === 'string' && TICKET_TYPE_GLYPH_PATTERN.test(value);
}

/** The recognised five, for anything that needs to know which is which. */
export function isKnownBugVariant(value: unknown): value is BugVariantName {
  return typeof value === 'string' && BUG_VARIANT_SET.has(value);
}

/**
 * Pick a species for a new bug pin (TOADO-313).
 *
 * Uniform over the five, rolled once here at creation and stored on the
 * shape. Never recomputed at render: a marker that changes species on reload
 * is its own bug report. Repeats on one page are expected rather than avoided
 * with five icons, and the icon is decorative variety, not a legend.
 */
export function randomBugVariant(): string {
  return BUG_VARIANTS[Math.floor(Math.random() * BUG_VARIANTS.length)] ?? BUG_VARIANTS[0];
}

/**
 * The image id of a ticket's original capture slot. Shapes that predate
 * per-image scoping are normalized onto this id (see `normalizeShape`).
 */
export const ORIGINAL_IMAGE_ID = 'original';

/**
 * Each shape is pinned to a specific image (carousel slot) so annotations
 * stay scoped to the image they were drawn on, not bleed across every
 * image in the ticket. `imageId` is optional on the type for backward
 * compatibility with snapshots that pre-date this change (those are
 * normalized to 'original' on hydration — see normalizeShape below).
 * New shapes are always tagged at creation.
 *
 * This union is the canonical shape model shared by every annotation
 * surface (web post-submit canvas, extension overlay, widget, v2
 * timeline). Snapshots of `Shape[]` are what the server stores and what
 * `annotation.updated` events carry — changing a variant here is a wire
 * format change, so additions must stay backward-compatible with
 * persisted snapshots.
 */
export type Shape =
  | { id: string; imageId?: string; kind: 'stroke'; tool: 'pen' | 'highlighter'; color: HighlightColor; width: number; points: Array<[number, number]> }
  | { id: string; imageId?: string; kind: 'circle'; color: HighlightColor; x: number; y: number; w: number; h: number }
  | { id: string; imageId?: string; kind: 'arrow'; color: HighlightColor; x1: number; y1: number; x2: number; y2: number }
  | { id: string; imageId?: string; kind: 'redact'; x: number; y: number; w: number; h: number }
  | { id: string; imageId?: string; kind: 'blur'; x: number; y: number; w: number; h: number; radius?: number }
  | {
    id: string; imageId?: string; kind: 'pin'; x: number; y: number;
    style: PinStyle; number?: number; bugVariant?: string;
    /**
     * The marker's body colour, as `#rrggbb`.
     *
     * Absent on every pin saved before this existed, which is why it is
     * optional and why the renderer has a default rather than this having a
     * value: a pin written by an older extension has to keep drawing, and
     * the colour it drew in is the violet that used to be hard-coded.
     */
    color?: string;
  }
  | { id: string; imageId?: string; kind: 'textBox'; x: number; y: number; w: number; h: number; text: string; variant: TextVariant; style: TextStyle };

/** The canonical screenshot marker written by extension pin capture. */
export type PinShape = Extract<Shape, { kind: 'pin' }>;

/**
 * The unknown-kind round-trip rule (IN_PAGE_ANNOTATION_PRD 8.2).
 *
 * A client that meets a shape kind it does not know must ignore it for
 * rendering AND preserve it verbatim on save. Version drift is
 * guaranteed here, not hypothetical: the extension ships through a
 * multi-day CWS review queue while web deploys on push, so the two are
 * routinely out of step - without this rule, an older web client
 * silently destroys a newer extension's shapes the first time someone
 * edits an annotation.
 *
 * What that means in code: snapshots loaded from the server may contain
 * kinds outside the `Shape` union. `normalizeShape` and `rebaseShapes`
 * are generic and pass them through untouched; `drawShape` and
 * `shapeHit` no-op on kinds they do not recognize; and any DESTRUCTIVE
 * operation scoped to "everything on this image" (clear-all, undo
 * scanning) must use `isKnownShapeKind` so it never deletes what the
 * user cannot see. Saves always send the full array, unknown kinds
 * included.
 */
export const KNOWN_SHAPE_KINDS: ReadonlySet<string> = new Set([
  'stroke', 'circle', 'arrow', 'redact', 'blur', 'pin', 'textBox',
]);

/**
 * A shape from a snapshot that may have been written by a newer client:
 * either a kind this build knows, or an opaque record to carry through.
 */
export type SnapshotShape = Shape | (Record<string, unknown> & { id: string; imageId?: string; kind: string });

export function isKnownShapeKind(s: SnapshotShape): s is Shape {
  return KNOWN_SHAPE_KINDS.has(s.kind);
}

/** Runtime guard for snapshot style values written by other client versions. */
export function isPinStyle(value: unknown): value is PinStyle {
  return typeof value === 'string' && (PIN_STYLES as readonly string[]).includes(value);
}

/** Runtime boundary for untrusted annotation snapshots entering DOM editing. */
export function isRenderablePinShape(value: unknown): value is PinShape {
  if (!value || typeof value !== 'object') return false;
  const pin = value as Record<string, unknown>;
  return pin.kind === 'pin'
    && typeof pin.id === 'string'
    && typeof pin.x === 'number' && Number.isFinite(pin.x)
    && typeof pin.y === 'number' && Number.isFinite(pin.y)
    && isPinStyle(pin.style)
    && (pin.number === undefined || (typeof pin.number === 'number' && Number.isFinite(pin.number)))
    // A variant this build does not know is not a reason to refuse the pin:
    // it draws the generic bug. A variant that is not a string at all is,
    // because nothing downstream should have to consider one.
    && (pin.bugVariant === undefined || typeof pin.bugVariant === 'string')
    // A colour is either a hex this renderer can use or it is not there.
    // Anything else - a name, a gradient, a CSS variable - would reach a
    // canvas `fillStyle` that silently ignores what it cannot parse and
    // draws the previous colour instead, which on a shared canvas is
    // whatever the last shape happened to set.
    && (pin.color === undefined || isPinColor(pin.color));
}

/** `#rrggbb`, and nothing else. */
export function isPinColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);
}

/** Number painted and announced for a numbered pin, including old bad data. */
export function pinDisplayNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(99, Math.max(1, Math.round(value)))
    : 1;
}

/** The axis-aligned-rectangle shapes that share move/resize handling. */
export type RectShape = Extract<Shape, { kind: 'textBox' | 'redact' | 'blur' }>;

export function isRectShape(s: Shape): s is RectShape {
  return s.kind === 'textBox' || s.kind === 'redact' || s.kind === 'blur';
}

export function shapeId(): string { return 's-' + Math.random().toString(36).slice(2, 10); }

/**
 * Backfill `imageId` on shapes loaded from server or localStorage that
 * pre-date the per-image scoping change. Existing annotations were always
 * drawn on the original capture, so pinning them there preserves
 * visibility on first load.
 */
export function normalizeShape(s: Shape): Shape;
export function normalizeShape(s: SnapshotShape): SnapshotShape;
export function normalizeShape(s: SnapshotShape): SnapshotShape {
  // Empty string is the intentional image id for a text-only ticket with
  // no capture slot. Only snapshots that truly predate image scoping omit
  // the property and should be assigned to the original capture.
  // Generic (not Shape) so unknown-kind shapes from newer clients pass
  // through verbatim per the round-trip rule above.
  const scoped = s.imageId !== undefined ? s : { ...s, imageId: ORIGINAL_IMAGE_ID };
  const candidate = scoped as SnapshotShape & { x?: unknown; y?: unknown; style?: unknown };
  // `marker` was the pre-TOADO-264 wire spelling. Convert only the exact
  // known legacy contract; malformed or future marker-like records remain
  // opaque so the unknown-kind round-trip guarantee still holds.
  if (candidate.kind === 'marker') {
    const canonical = { ...scoped, kind: 'pin' };
    if (isRenderablePinShape(canonical)) return canonical;
  }
  return scoped;
}

/**
 * Three-way merge for shape stacks when a remote annotation.updated
 * arrives while the local user has unsaved edits.
 *
 *   base    = the shapes the local client last saw confirmed by the
 *             server (initialShapes on mount, or the snapshot we
 *             successfully POSTed).
 *   current = what's in the local editor right now.
 *   remote  = the snapshot the teammate just saved.
 *
 * Local additions (in current, not in base) survive — they get
 * appended after the remote snapshot. Local deletions (in base, not in
 * current) survive — those ids are stripped from the remote snapshot
 * before merging.
 *
 * Same-id local modifications also survive as unsynced local upserts.
 * Shapes the local user did not touch take the remote value. This is a
 * shape-level three-way merge, not a field-level CRDT: if both sides
 * changed one shape, the pending local shape wins until saved or discarded.
 */
export function rebaseShapes<T extends { id: string }>(remote: T[], current: T[], base: T[]): T[] {
  const baseById = new Map(base.map((shape) => [shape.id, shape]));
  const currentIds = new Set(current.map((s) => s.id));
  const localDeletionIds = new Set(
    base.filter((s) => !currentIds.has(s.id)).map((s) => s.id),
  );
  const localUpserts = current.filter((shape) => {
    const baseShape = baseById.get(shape.id);
    return baseShape === undefined || !snapshotValueEqual(shape, baseShape);
  });
  const localUpsertById = new Map(localUpserts.map((shape) => [shape.id, shape]));
  const remoteIds = new Set(remote.map((shape) => shape.id));
  const mergedRemote = remote
    .filter((shape) => !localDeletionIds.has(shape.id))
    .map((shape) => localUpsertById.get(shape.id) ?? shape);
  const missingLocalUpserts = localUpserts.filter((shape) => !remoteIds.has(shape.id));
  return [...mergedRemote, ...missingLocalUpserts];
}

/** Structural equality for JSON-compatible snapshot values. */
export function snapshotValueEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (typeof left !== 'object' || left === null || typeof right !== 'object' || right === null) return false;
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    return left.every((value, index) => snapshotValueEqual(value, right[index]));
  }
  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;
  const leftKeys = Object.keys(leftRecord).sort();
  const rightKeys = Object.keys(rightRecord).sort();
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key, index) => key === rightKeys[index]
    && snapshotValueEqual(leftRecord[key], rightRecord[key]));
}

/**
 * How a ticket's pin should be drawn, read out of its annotation snapshot.
 *
 * Shared because three places needed the same answer and two of them had it
 * byte-identically copied: the anchored-ticket list, the cross-page pin index,
 * and the control's own row parser. The rule it encodes is the load-bearing
 * decision of TOADO-297 - pass a species through on its shape, never on
 * whether this build recognises it - and a rule that lives in three files is
 * a rule that gets updated in two.
 */
export function pinPresentation(snapshot: unknown): {
  style: PinStyle;
  number?: number;
  bugVariant?: string;
  color?: string;
} {
  if (!Array.isArray(snapshot)) return { style: 'pin' };
  const pin = snapshot.find(isRenderablePinShape);
  if (!pin) return { style: 'pin' };
  return {
    style: pin.style,
    ...(typeof pin.number === 'number' && Number.isFinite(pin.number) ? { number: pin.number } : {}),
    ...(isBugVariant(pin.bugVariant) ? { bugVariant: pin.bugVariant } : {}),
    // The colour it was saved in, so the marker on the page matches the
    // marker in the screenshot. Without this the two disagree the moment
    // anybody uses the colour picker: one reads the shape, the other has
    // only a style.
    ...(isPinColor(pin.color) ? { color: pin.color } : {}),
  };
}
